import { PageSpeedMetric } from './pageSpeedMetric';
import { ERROR_SIGIL } from './constants';

import { onCLS, onFID, onLCP, onTTFB } from 'web-vitals';

import type {
    IPerfkit,
    Metric,
    WebVitalMetric,
    IPageSpeedMetric,
} from './types';
import type { Logger } from '@amp/web-apps-logger';
import type { MTPerfkit } from '@amp-metrics/mt-perfkit';

/**
 * Adapter class to use "@amp-metrics/mt-perfkit" in mono-repo
 */
export class PerfKit implements IPerfkit {
    private readonly mtPerfkit: Promise<MTPerfkit | null>;
    private readonly pageType: Promise<string>;
    private pageTypeResolver:
        | ((value: string | PromiseLike<string>) => void)
        | null;
    private isPageTypeSet: boolean;
    private readonly logger: Logger;
    private observer?: PerformanceObserver;
    private webVitals: Set<Metric>;
    private isPerfkitReady: boolean;
    private pageSpeedMetric: IPageSpeedMetric | null;
    constructor(mtPerfkit: Promise<MTPerfkit | null>, log: Logger) {
        this.mtPerfkit = mtPerfkit;
        this.logger = log;
        this.isPerfkitReady = false;
        this.webVitals = new Set();
        this.pageSpeedMetric = null;
        this.pageTypeResolver = null;
        this.isPageTypeSet = false;
        this.pageType = new Promise((res, _rej) => {
            this.pageTypeResolver = res;
            // timeout if we dont set the pageType
            window.setTimeout(() => {
                if (!this.isPageTypeSet) {
                    this.logger.debug('failed to set pageType');
                    this.isPageTypeSet = true;
                    res(ERROR_SIGIL);
                }
            }, 10000);
        });

        this.mtPerfkit.then((perfkit) => {
            if (perfkit !== null) {
                this.isPerfkitReady = true;
                log.debug('perfkit is ready');
                this.flushWebVitals(perfkit);
            }
        });
    }

    private recordLoadURLEvents(
        mtPerfkit: MTPerfkit,
        entries: PerformanceEntryList,
    ) {
        for (const entry of entries) {
            const entryJSON = {
                ...entry.toJSON(),
                isSSL: entry.name.startsWith('https'),
            };

            const loadUrlObject =
                mtPerfkit.eventHandlers.loadUrl.fromTimingEntry(entryJSON);

            loadUrlObject.recordEvent();
        }
    }

    private flushWebVitals(perfKit: MTPerfkit) {
        this.webVitals.forEach((metric) => {
            this.recordWebVital(metric, perfKit);
        });
        this.webVitals.clear();
    }

    private recordWebVital(metric: Metric, perfKit: MTPerfkit) {
        if (perfKit === null) {
            return;
        }
        const pageUrl = window.location.href;

        this.getPageType().then((type) => {
            const pageType = type;
            this.logger.debug(
                `web_vital:${metric.name}, pageType:${pageType} captured`,
                metric,
            );
            const metricData: WebVitalMetric = {
                id: metric.id,
                value: metric.value,
                rating: metric.rating,
                navigationType: metric.navigationType,
                pageUrl,
                pageType: type,
            };
            const event = perfKit.eventHandlers.flexible.instance(
                metric.name,
                metricData,
            );
            event.recordEvent();
        });
    }

    private onWebVitalReport(metric: Metric) {
        if (this.isPerfkitReady) {
            this.mtPerfkit.then((perfKit) => {
                this.recordWebVital(metric, perfKit!);
            });
        } else {
            let containsMetric = false;
            for (const metric of this.webVitals) {
                if (metric.name === metric.name) {
                    containsMetric = true;
                    break;
                }
            }
            if (!containsMetric) {
                this.webVitals.add(metric);
            }
        }
    }

    public async setupLoadUrlEventObserver() {
        const perfkitInstance = await this.mtPerfkit;

        if (perfkitInstance === null) {
            return;
        }

        if (window.performance.getEntriesByType) {
            this.recordLoadURLEvents(
                perfkitInstance,
                window.performance.getEntriesByType('resource'),
            );
        }

        if ('PerformanceObserver' in window === false) {
            this.logger.warn(
                'PerformanceObserver is not supported. Some metrics may not be captured',
            );
            return;
        }

        this.observer = new PerformanceObserver((list) => {
            this.recordLoadURLEvents(perfkitInstance, list.getEntries());
        });

        this.observer.observe({ entryTypes: ['resource'] });
    }

    public setupWebVitals() {
        const onWebVitalReport = this.onWebVitalReport.bind(this);
        onCLS(onWebVitalReport);
        onFID(onWebVitalReport);
        onLCP(onWebVitalReport);
        onTTFB(onWebVitalReport);
    }

    public getPageType(): Promise<string> {
        return this.pageType;
    }

    public setPageType(pageType: string) {
        if (!this.isPageTypeSet) {
            const type = pageType ?? ERROR_SIGIL;
            this.pageTypeResolver!(type);
            this.isPageTypeSet = true;
        }
    }

    public getPageSpeedMetric(): IPageSpeedMetric | null {
        return this.pageSpeedMetric;
    }

    public deletePageSpeedMetric(): void {
        this.pageSpeedMetric = null;
    }

    public makeNewPageSpeedMetric(): IPageSpeedMetric {
        this.pageSpeedMetric = new PageSpeedMetric(
            this.mtPerfkit,
            this.logger,
            this.deletePageSpeedMetric.bind(this),
        );

        return this.getPageSpeedMetric()!;
    }
}
